/**
 * NetworkSearch — Search the Lending Network by Bank or Loan Product
 *
 * Allows agents to find colleagues by:
 *   - Bank / NBFC name  (e.g. "Axis Bank", "HDFC Bank")
 *   - Loan product      (e.g. "Home Loans", "Personal Loans")
 *   - Agent name (free text)
 *
 * Results show full agent cards with an inline Connect button.
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { Divider } from 'primereact/divider';
import { Toast } from 'primereact/toast';
import axiosInstance from '../../config/axios.config';
import ConnectionService from '../../services/agentProfile/ConnectionService';
import { LOAN_PRODUCTS as PRODUCT_CATALOG, productLabel } from './profileCatalogs';

// ─── Static filter data ────────────────────────────────────────────────────

// Canonical loan products — value is the product_type the backend matches
// against each agent's declared product expertise (products they handle).
const LOAN_PRODUCTS = PRODUCT_CATALOG.map((p) => ({ label: p.label, value: p.value }));

// ─── Component ─────────────────────────────────────────────────────────────

const NetworkSearch = ({ onConnectionSent }) => {
    const toast = useRef(null);
    const navigate = useNavigate();

    // Dynamic companies list (loaded from DB on mount)
    const [companies, setCompanies] = useState([]);
    // Dynamic states list — only states that actually have agents, so the
    // dropdown answers "which states have agents present" on its own.
    const [states, setStates] = useState([]);

    useEffect(() => {
        axiosInstance.get('/leadflow/network/companies')
            .then(res => {
                if (res.data.success && Array.isArray(res.data.data)) {
                    setCompanies(res.data.data.map(c => ({ label: c, value: c })));
                }
            })
            .catch(() => {
                // silently ignore — dropdown will just be empty
            });

        axiosInstance.get('/leadflow/network/states')
            .then(res => {
                if (res.data.success && Array.isArray(res.data.data)) {
                    setStates(res.data.data.map(s => ({ label: s, value: s })));
                }
            })
            .catch(() => {
                // silently ignore — dropdown will just be empty
            });
    }, []);

    // Filters
    const [bank, setBank] = useState(null);
    const [product, setProduct] = useState(null);
    const [state, setState] = useState(null);
    const [nameQuery, setNameQuery] = useState('');

    // Results
    const [results, setResults] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    // Connection states
    const [connectingId, setConnectingId] = useState(null);
    const [connectedIds, setConnectedIds] = useState(new Set());

    const hasFilters = bank || product || state || nameQuery.trim();

    const runSearch = async (page = 1, append = false) => {
        if (!hasFilters) return;

        if (append) setLoadingMore(true);
        else { setLoading(true); setResults([]); }

        try {
            const params = new URLSearchParams();
            if (bank)              params.append('bank', bank);
            if (product)           params.append('product', product);
            if (state)             params.append('state', state);
            if (nameQuery.trim())  params.append('q', nameQuery.trim());
            params.append('limit', 20);
            params.append('page', page);

            const response = await axiosInstance.get(
                `/leadflow/network/search?${params.toString()}`
            );

            if (response.data.success) {
                const { results: newResults, pagination: pag } = response.data.data;
                setResults(prev => append ? [...prev, ...newResults] : newResults);
                setPagination(pag);
                setCurrentPage(page);
                setSearched(true);
            } else {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'No results',
                    detail: response.data.message || 'No agents found.',
                    life: 3000,
                });
                if (!append) { setResults([]); setSearched(true); }
            }
        } catch (error) {
            const msg = error.response?.data?.message || 'Search failed. Please try again.';
            toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleClear = () => {
        setBank(null);
        setProduct(null);
        setState(null);
        setNameQuery('');
        setResults([]);
        setPagination(null);
        setSearched(false);
        setCurrentPage(1);
    };

    const handleConnect = async (person) => {
        if (connectingId) return;
        setConnectingId(person.id);
        try {
            const res = await ConnectionService.sendRequest({
                receiver_profile_id: parseInt(person.id),
                connection_message: null,
            });
            if (res.success) {
                setConnectedIds(prev => new Set([...prev, person.id]));
                toast.current?.show({
                    severity: 'success',
                    summary: 'Request Sent',
                    detail: `Connection request sent to ${person.full_name}`,
                    life: 3000,
                });
                if (onConnectionSent) onConnectionSent();
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: res.message || 'Failed to send request',
                    life: 3000,
                });
            }
        } catch (err) {
            const msg = err.response?.data?.message || '';
            if (msg.toLowerCase().includes('already') || err.response?.status === 409 || err.response?.status === 422) {
                setConnectedIds(prev => new Set([...prev, person.id]));
                toast.current?.show({
                    severity: 'info',
                    summary: 'Already Connected',
                    detail: 'You already have a connection or pending request with this person.',
                    life: 3000,
                });
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to send connection request',
                    life: 3000,
                });
            }
        } finally {
            setConnectingId(null);
        }
    };

    // ─── Render helpers ───────────────────────────────────────────────────

    const renderSkeletonGrid = () => (
        <div className="grid">
            {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="col-12 md:col-6 lg:col-4">
                    <div className="border-1 surface-border border-round p-3">
                        <div className="flex gap-3 align-items-start mb-3">
                            <Skeleton shape="circle" size="3.5rem" />
                            <div className="flex-1">
                                <Skeleton className="mb-2" height="1rem" width="70%" />
                                <Skeleton className="mb-1" height="0.8rem" width="90%" />
                                <Skeleton height="0.8rem" width="50%" />
                            </div>
                        </div>
                        <Skeleton height="2rem" borderRadius="6px" />
                    </div>
                </div>
            ))}
        </div>
    );

    const renderAgentCard = (person) => {
        const isConnecting = connectingId === person.id;
        // Already accepted connection → offer Message instead of Connect.
        const isConnected = person.connection_status === 'connected';
        // A request is outstanding — either we just sent one this session
        // (connectedIds) or the backend reported a pending one.
        const requestSent = connectedIds.has(person.id) || person.connection_status === 'pending_sent';
        const requestReceived = person.connection_status === 'pending_received';
        const location = [person.location_city, person.location_state].filter(Boolean).join(', ');

        return (
            <div key={person.id} className="col-12 md:col-6 lg:col-4">
                <div className="border-1 surface-border border-round p-3 h-full flex flex-column justify-content-between hover:shadow-2 transition-shadow transition-duration-200">
                    {/* Top: avatar + info */}
                    <div>
                        <div className="flex gap-3 align-items-start mb-3">
                            <Avatar
                                image={person.profile_photo_url}
                                label={person.full_name?.charAt(0)?.toUpperCase()}
                                shape="circle"
                                size="large"
                                className="flex-shrink-0 cursor-pointer"
                                onClick={() => navigate(`/app/profile/${person.id}`)}
                            />
                            <div className="min-w-0 flex-1">
                                <div
                                    className="font-semibold text-900 text-sm cursor-pointer hover:text-primary transition-colors transition-duration-150 flex align-items-center gap-1"
                                    onClick={() => navigate(`/app/profile/${person.id}`)}
                                >
                                    {person.full_name}
                                    {person.is_verified && (
                                        <i className="pi pi-verified text-primary" style={{ fontSize: '0.8rem' }} title="Verified" />
                                    )}
                                </div>

                                {person.current_designation && (
                                    <div className="text-xs text-color-secondary mt-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                        {person.current_designation}
                                    </div>
                                )}

                                {person.current_company && (
                                    <div className="flex align-items-center gap-1 mt-1">
                                        <i className="pi pi-building text-xs" style={{ color: 'var(--primary-color)' }} />
                                        <span className="text-xs font-medium text-primary">{person.current_company}</span>
                                    </div>
                                )}

                                {location && (
                                    <div className="flex align-items-center gap-1 mt-1">
                                        <i className="pi pi-map-marker text-xs text-color-secondary" />
                                        <span className="text-xs text-color-secondary">{location}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Headline */}
                        {person.headline && (
                            <p className="text-xs text-color-secondary m-0 mb-2 line-clamp-2" style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}>
                                {person.headline}
                            </p>
                        )}

                        {/* Specializations */}
                        {person.specializations?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                                {person.specializations.slice(0, 3).map((s, i) => (
                                    <Tag key={i} value={s} severity="info" className="text-xs" style={{ fontSize: '0.65rem' }} />
                                ))}
                                {person.specializations.length > 3 && (
                                    <Tag value={`+${person.specializations.length - 3}`} severity="secondary" className="text-xs" style={{ fontSize: '0.65rem' }} />
                                )}
                            </div>
                        )}

                        {/* Stats */}
                        <div className="flex gap-3 text-xs text-color-secondary mb-3">
                            {person.experience_years > 0 && (
                                <span><i className="pi pi-briefcase mr-1" />{person.experience_years}y exp</span>
                            )}
                            {person.connection_count > 0 && (
                                <span><i className="pi pi-users mr-1" />{person.connection_count} connections</span>
                            )}
                        </div>
                    </div>

                    {/* Action */}
                    <div className="flex gap-2">
                        <Button
                            label="View Profile"
                            icon="pi pi-user"
                            className="p-button-outlined p-button-sm flex-1"
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => navigate(`/app/profile/${person.id}`)}
                        />
                        {isConnected ? (
                            // Already connected → message them directly.
                            <Button
                                icon="pi pi-comments"
                                label="Message"
                                className="p-button-sm flex-1"
                                style={{ fontSize: '0.75rem' }}
                                onClick={() => navigate(`/app/messaging?profileId=${person.id}`)}
                            />
                        ) : requestReceived ? (
                            // They already asked to connect with us → send them to
                            // the profile to accept/ignore the pending request.
                            <Button
                                icon="pi pi-user-plus"
                                label="Respond"
                                className="p-button-sm p-button-outlined flex-1"
                                style={{ fontSize: '0.75rem' }}
                                onClick={() => navigate(`/app/profile/${person.id}`)}
                            />
                        ) : requestSent ? (
                            <Button
                                icon="pi pi-check"
                                label="Sent"
                                className="p-button-sm p-button-outlined p-button-secondary flex-1"
                                style={{ fontSize: '0.75rem' }}
                                disabled
                            />
                        ) : (
                            <Button
                                icon={isConnecting ? 'pi pi-spin pi-spinner' : 'pi pi-user-plus'}
                                label="Connect"
                                className="p-button-sm flex-1"
                                style={{ fontSize: '0.75rem' }}
                                onClick={() => handleConnect(person)}
                                disabled={!!connectingId}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ─── Main render ──────────────────────────────────────────────────────

    return (
        <>
            <Toast ref={toast} />

            {/* Search Filters */}
            <Card className="mb-3">
                <div className="flex align-items-center gap-2 mb-3">
                    <i className="pi pi-search text-primary text-xl" />
                    <h5 className="m-0 text-900">Find Agents in the Lending Network</h5>
                </div>

                <div className="grid">
                    {/* Bank filter */}
                    <div className="col-12 md:col-6 lg:col-3">
                        <label className="block text-sm font-medium mb-1 text-color-secondary">
                            <i className="pi pi-building mr-1" />Bank / NBFC
                        </label>
                        <Dropdown
                            value={bank}
                            options={companies}
                            onChange={e => setBank(e.value)}
                            placeholder={companies.length === 0 ? 'Loading...' : 'Select company'}
                            className="w-full"
                            filter
                            showClear
                            filterPlaceholder="Type to filter..."
                            emptyMessage="No companies found"
                        />
                    </div>

                    {/* State filter */}
                    <div className="col-12 md:col-6 lg:col-3">
                        <label className="block text-sm font-medium mb-1 text-color-secondary">
                            <i className="pi pi-map-marker mr-1" />State
                        </label>
                        <Dropdown
                            value={state}
                            options={states}
                            onChange={e => setState(e.value)}
                            placeholder={states.length === 0 ? 'Loading...' : 'Select state'}
                            className="w-full"
                            filter
                            showClear
                            filterPlaceholder="Type to filter..."
                            emptyMessage="No states found"
                        />
                    </div>

                    {/* Product filter */}
                    <div className="col-12 md:col-6 lg:col-3">
                        <label className="block text-sm font-medium mb-1 text-color-secondary">
                            <i className="pi pi-tag mr-1" />Loan Product
                        </label>
                        <Dropdown
                            value={product}
                            options={LOAN_PRODUCTS}
                            optionLabel="label"
                            optionValue="value"
                            onChange={e => setProduct(e.value)}
                            placeholder="e.g. Home Loan"
                            className="w-full"
                            filter
                            showClear
                            filterPlaceholder="Type to search product..."
                        />
                    </div>

                    {/* Name search */}
                    <div className="col-12 md:col-6 lg:col-3">
                        <label className="block text-sm font-medium mb-1 text-color-secondary">
                            <i className="pi pi-user mr-1" />Name or Email
                        </label>
                        <InputText
                            value={nameQuery}
                            onChange={e => setNameQuery(e.target.value)}
                            placeholder="Search by name or email..."
                            className="w-full"
                            onKeyDown={e => e.key === 'Enter' && runSearch(1)}
                        />
                    </div>
                </div>

                <Divider className="my-2" />

                <div className="flex gap-2 justify-content-end">
                    {(bank || product || state || nameQuery || searched) && (
                        <Button
                            label="Clear"
                            icon="pi pi-times"
                            className="p-button-outlined p-button-secondary p-button-sm"
                            onClick={handleClear}
                        />
                    )}
                    <Button
                        label="Search"
                        icon="pi pi-search"
                        className="p-button-sm"
                        onClick={() => runSearch(1)}
                        disabled={!hasFilters || loading}
                        loading={loading}
                    />
                </div>
            </Card>

            {/* Results */}
            {loading && renderSkeletonGrid()}

            {!loading && searched && (
                <>
                    {/* Result count header */}
                    <div className="flex align-items-center justify-content-between mb-3">
                        <span className="text-color-secondary text-sm">
                            {pagination?.total > 0
                                ? `${pagination.total} agent${pagination.total !== 1 ? 's' : ''} found`
                                : 'No agents found'}
                            {bank && <> at <strong>{bank}</strong></>}
                            {state && <> in <strong>{state}</strong></>}
                            {product && <> for <strong>{productLabel(product)}</strong></>}
                        </span>
                        {results.length > 0 && pagination && (
                            <span className="text-xs text-color-secondary">
                                Showing {results.length} of {pagination.total}
                            </span>
                        )}
                    </div>

                    {results.length > 0 ? (
                        <>
                            <div className="grid">
                                {results.map(renderAgentCard)}
                            </div>

                            {/* Load more */}
                            {pagination && currentPage < pagination.last_page && (
                                <div className="flex justify-content-center mt-4">
                                    <Button
                                        label={loadingMore ? 'Loading...' : 'Load More'}
                                        icon={loadingMore ? 'pi pi-spin pi-spinner' : 'pi pi-chevron-down'}
                                        className="p-button-outlined p-button-secondary"
                                        onClick={() => runSearch(currentPage + 1, true)}
                                        disabled={loadingMore}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-6">
                            <i className="pi pi-search text-5xl text-400 mb-3 block" />
                            <h4 className="text-900 mb-2">No agents found</h4>
                            <p className="text-color-secondary m-0 text-sm">
                                Try a different bank, state, loan product, or agent name.
                            </p>
                        </div>
                    )}
                </>
            )}

            {/* Initial empty state */}
            {!loading && !searched && (
                <div className="text-center py-6 text-color-secondary">
                    <i className="pi pi-building text-5xl text-400 mb-3 block" />
                    <p className="m-0 text-sm">
                        Select a bank, state, or loan product — or type an agent's name — to find agents in the Lending Network.
                    </p>
                </div>
            )}
        </>
    );
};

export default NetworkSearch;
